import { useMemo, useState } from 'react';
import ErrorBoundary from '../../components/ErrorBoundary';
import LoginHOC from '../../components/LoginHOC';
import IntrospectSession from './components/IntrospectSession';
import BuildSelector from './components/BuildSelector';
import PlatformSelector from './components/PlatformSelector';
import LatestBuildLoader from './components/LatestBuildLoader';
import { useAnalyticsCommand } from '../../utilities/useAnalytics';
import { normalizePlatform } from './utilities/sessionManager';
import getProjectConfig from '../../utilities/projectConfig/getProjectConfig';

export default function IntrospectCommand({
  buildId: initialBuildId,
  platform: initialPlatform,
  verbose,
  latest,
  configPath,
}: {
  buildId?: string;
  platform?: string;
  verbose?: boolean;
  latest?: boolean;
  configPath?: string;
}) {
  const [buildId, setBuildId] = useState<string | undefined>(initialBuildId);
  const [platform, setPlatform] = useState<string | undefined>(initialPlatform);
  const appId = useMemo(() => {
    try {
      return getProjectConfig(configPath).config.id;
    } catch {
      return undefined;
    }
  }, [configPath]);

  useAnalyticsCommand('introspect', {
    buildId,
    platform,
    latest,
    hasInitialBuildId: !!initialBuildId,
    hasInitialPlatform: !!initialPlatform,
  });

  const renderContent = () => {
    if (latest && !buildId) {
      return (
        <LatestBuildLoader
          configPath={configPath}
          onBuildFound={(id) => setBuildId(id)}
        />
      );
    }

    if (!buildId) {
      return (
        <BuildSelector
          configPath={configPath}
          onSelect={(id) => setBuildId(id)}
        />
      );
    }

    if (!platform) {
      return (
        <PlatformSelector
          buildId={buildId}
          configPath={configPath}
          onSelect={(p) => setPlatform(p)}
        />
      );
    }

    const normalizedPlatform = normalizePlatform(platform);

    return (
      <IntrospectSession
        buildId={buildId}
        platform={normalizedPlatform}
        verbose={verbose ?? false}
        appId={appId}
      />
    );
  };

  return (
    <ErrorBoundary>
      <LoginHOC>{renderContent()}</LoginHOC>
    </ErrorBoundary>
  );
}
